import type {State} from '../reducers/types'
import {getBuild, getBuildType, getProject} from '../selectors'
import type {BuildType} from '../services/rest'
import type {BuildId, BuildTypeId, NormalizedBuild, NormalizedProject, ProjectId} from '../types'

import {useAppSelector} from 'src/hooks/react-redux'

export function useBuildData<R>(
  {
    buildId,
  }: {
    buildId?: BuildId
  },
  selector: (build: NormalizedBuild | null | undefined) => R,
): R {
  return useAppSelector((state: State) => selector(getBuild(state, buildId)))
}

export function useBuildTypeData<R>(
  {
    buildId,
    ...otherParams
  }: {
    buildId?: BuildId
    buildTypeId?: BuildTypeId
  },
  selector: (buildType: BuildType | null | undefined) => R,
): R {
  const buildTypeIdFromBuild = useBuildData({buildId}, build => build?.buildType)
  return useAppSelector((state: State) =>
    selector(getBuildType(state, otherParams.buildTypeId ?? buildTypeIdFromBuild)),
  )
}

export function useProjectData<R>(
  params: {
    buildId?: BuildId
    buildTypeId?: BuildTypeId
    projectId?: ProjectId
  },
  selector: (project: NormalizedProject | null | undefined) => R,
): R {
  const {buildId, buildTypeId} = params
  const projectIdFromBuildType = useBuildTypeData(
    {buildId, buildTypeId},
    buildType => buildType?.projectId,
  )
  return useAppSelector((state: State) =>
    selector(getProject(state, params.projectId ?? projectIdFromBuildType)),
  )
}
